import { AnimationEvent } from '@angular/animations';
import { ConfigurableFocusTrapFactory, FocusTrap } from '@angular/cdk/a11y';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';

import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostBinding,
  Inject,
  Optional,
  ViewChild,
  DOCUMENT
} from '@angular/core';
import { Client } from '@df/core/service/client';
import { TimeoutService } from '@df/ng/timeout.service';
import { ModalConfig } from '@df/ui/modal/modal-config';

/**
 * Throws an exception for the case when a ComponentPortal is
 * attached to a DomPortalOutlet without an origin.
 */
export function throwModalContentAlreadyAttachedError(): Error {
    throw Error('Attempting to attach modal content after content is already attached');
}

@Directive()
export abstract class ModalContainerAbstract extends BasePortalOutlet {
    /** The portal outlet inside of this container into which the modal content will be loaded. */
    @ViewChild(CdkPortalOutlet, { static: true }) portalOutlet!: CdkPortalOutlet;

    @HostBinding('class') get modalClass(): string | string[] {
        return this.config.modalClass || '';
    }

    /** The class that traps and manages focus within the modal. */
    protected focusTrap?: FocusTrap;

    /** Element that was focused before the modal was opened. Save this to restore upon close. */
    protected elementFocusedBeforeModalWasOpened: HTMLElement | null = null;

    /** The state of the bottom sheet animations. */
    animationState: 'void' | 'enter' | 'exit' = 'enter';

    /** Emits when an animation state changes. */
    animationStateChanged = new EventEmitter<AnimationEvent>();

    /** ID of the element that should be considered as the modal's label. */
    ariaLabelledBy: string | null;

    /** ID for the container DOM element. */
    id?: string;

    constructor(
        protected elementRef: ElementRef,
        protected focusTrapFactory: ConfigurableFocusTrapFactory,
        protected changeDetectorRef: ChangeDetectorRef,
        @Optional() @Inject(DOCUMENT) protected document: Document,
        /** The modal configuration. */
        public config: ModalConfig,
        private client: Client,
        private timeoutService: TimeoutService
    ) {
        super();
        this.ariaLabelledBy = config.ariaLabelledBy || null;
    }

    /** Initializes the dialog container with the attached content. */
    initializeWithAttachedContent(): void {
        this.timeoutService.async(() => {
            this.setupFocusTrap();
        });
        // Save the previously focused element. This element will be re-focused
        // when the dialog closes.
        this.timeoutService.async(() => {
            this.capturePreviouslyFocusedElement();
        });
    }

    /**
     * Attach a ComponentPortal as content to this modal container.
     * @param portal Portal to be attached as the modal content.
     */
    attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
        this.validatePortalAttached();

        return this.portalOutlet.attachComponentPortal(portal);
    }

    /**
     * Attach a TemplatePortal as content to this modal container.
     * @param portal Portal to be attached as the modal content.
     */
    attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
        this.validatePortalAttached();

        return this.portalOutlet.attachTemplatePortal(portal);
    }

    /** Sets up the focus trap. */
    protected setupFocusTrap(): void {
        if (!this.client.isCms && (this.config.trapFocus || (this.config.trapFocus === undefined && this.config.hasBackdrop))) {
            this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
        }
    }

    /** Moves the focus inside the focus trap. */
    protected trapFocus(): void {
        // If we were to attempt to focus immediately, then the content of the dialog would not yet be
        // ready in instances where change detection has to run first. To deal with this, we simply
        // wait for the microtask queue to be empty.
        if (this.config.autoFocus) {
            this.focusTrap?.focusInitialElementWhenReady();
        } else if (!this.containsFocus()) {
            this.elementRef.nativeElement.focus();
        }
    }

    /** Restores focus to the element that was focused before the modal opened. */
    protected restoreFocus(): void {
        const previousElement = this.elementFocusedBeforeModalWasOpened;

        // We need the extra check, because IE can set the `activeElement` to null in some cases.
        if (this.config.restoreFocus && previousElement && typeof previousElement.focus === 'function') {
            const activeElement = this.document.activeElement;
            const element = this.elementRef.nativeElement;

            // Make sure that focus is still inside the dialog or is on the body (usually because a
            // non-focusable element like the backdrop was clicked) before moving it. It's possible that
            // the consumer moved it themselves before the animation was done, in which case we shouldn't
            // do anything.
            if (!activeElement || activeElement === this.document.body || activeElement === element || element.contains(activeElement)) {
                previousElement.focus();
            }
        }

        if (this.focusTrap) {
            this.focusTrap.destroy();
        }
    }

    /** Captures the element that was focused before the dialog was opened. */
    protected capturePreviouslyFocusedElement(): void {
        if (this.document) {
            this.elementFocusedBeforeModalWasOpened = this.document.activeElement as HTMLElement;
        }
    }

    /** Returns whether focus is inside the dialog. */
    protected containsFocus(): boolean {
        const element = this.elementRef.nativeElement;
        const activeElement = this.document.activeElement;
        return element === activeElement || element.contains(activeElement);
    }

    /** Callback, invoked whenever an animation on the host completes. */
    onAnimationDone(event: AnimationEvent): void {
        if (event.toState === 'enter') {
            this.trapFocus();
        } else if (event.toState === 'exit') {
            this.restoreFocus();
        }

        this.animationStateChanged.emit(event);
    }

    /** Callback, invoked when an animation on the host starts. */
    onAnimationStart(event: AnimationEvent): void {
        this.animationStateChanged.emit(event);
    }

    /** Starts the modal exit animation. */
    startExitAnimation(): void {
        this.animationState = 'exit';

        // Mark the container for check so it can react if the
        // view container is using OnPush change detection.
        this.changeDetectorRef.markForCheck();
    }

    protected validatePortalAttached(): void | Error {
        if (this.portalOutlet.hasAttached()) {
            throw Error('Attempting to attach bottom modal content after content is already attached');
        }
    }

    scrollToTop(): void {
        const element = this.elementRef.nativeElement as HTMLElement;
        if (element) {
            element.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
}
